import { Component, inject, OnInit } from '@angular/core';
import { IonApp, IonRouterOutlet } from '@ionic/angular/standalone';
import { Router } from '@angular/router';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import OneSignalPlugin from '@onesignal/capacitor-plugin';
import { filter, take } from 'rxjs';
import { OneSignalService } from 'src/core/service/onesignal.service';
import { ForceUpdateService } from 'src/core/service/force-update.service';
import { AuthService } from 'src/core/service/auth.service';
import { LocationService } from 'src/core/service/location.service';
import { UserLocationRepository } from 'src/core/repositories/user-location.repository';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  imports: [IonApp, IonRouterOutlet],
})
export class AppComponent implements OnInit {
  private oneSignal     = inject(OneSignalService);
  private router        = inject(Router);
  private forceUpdate   = inject(ForceUpdateService);
  private authSvc       = inject(AuthService);
  private locationSvc   = inject(LocationService);
  private locationRepo  = inject(UserLocationRepository);

  async ngOnInit() {
    // Subscribe immediately so the shared user$ observable starts and caches
    // the native auth state — all later subscribers receive it without delay.
    this.authSvc.user$.subscribe();

    await this.oneSignal.initialize();
    this.oneSignal.trackAppOpen();
    this.setupDeepLinks();
    this.setupNotificationTap();
    this.setupForceUpdateCheck();
    this.setupFeaturedHappeningTracking();
    this.syncUserLocation();
    if (Capacitor.isNativePlatform()) {
      await StatusBar.setStyle({ style: Style.Light });
      await StatusBar.setOverlaysWebView({ overlay: true });
    }
    await this.forceUpdate.check();
  }

  private setupForceUpdateCheck() {
    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) this.forceUpdate.check();
    });
  }

  private setupFeaturedHappeningTracking() {
    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        this.oneSignal.trackAppOpen();
        this.oneSignal.checkNoBookingSession();
      } else {
        this.oneSignal.snapshotSessionOnBackground();
      }
    });
  }

  private setupDeepLinks() {
    App.addListener('appUrlOpen', ({ url }) => {
      const liveMatch = url.match(/\/(?:tabs\/)?live-stream\/([^/?#]+)/);
      if (liveMatch?.[1]) {
        this.router.navigate(['/tabs/live-stream', liveMatch[1]]);
        return;
      }
      // Match /h/{id} (share links) or /happening/{id} (legacy deep links)
      const hapMatch = url.match(/\/(?:h|happening)\/([^/?#]+)/);
      if (hapMatch?.[1]) {
        this.router.navigate(['/tabs/happening', hapMatch[1]]);
      }
    });
  }

  private syncUserLocation(): void {
    this.authSvc.user$.pipe(
      filter(user => !!user),
      take(1),
    ).subscribe(() => {
      this.locationSvc.getLocation()
        .then(({ lat, lng }) => this.locationRepo.saveIfStale(lat, lng))
        .catch(() => {});
    });
  }

  private setupNotificationTap() {
    if (!Capacitor.isNativePlatform()) return;
    OneSignalPlugin.Notifications.addEventListener('click', (event) => {
      const data = event.notification.additionalData as Record<string, string> | undefined;
      const type = data?.['type'];
      const reservationId = data?.['reservationId'];

      if (type === 'host_booking_received' || type === 'booking_request') {
        this.router.navigate(['/tabs/my-happenings'], { queryParams: { tab: 'hosting' } });
      } else if (reservationId) {
        this.router.navigate(['/tabs/reservation-status', reservationId]);
      }
    });
  }
}
